import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const VAPID_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
  return arr;
}

export function usePushNotifications() {
  const [permission, setPermission] = useState('default');
  const [subscription, setSubscription] = useState(null);
  const [supported, setSupported] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const isSupported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    setSupported(isSupported);
    if (isSupported) {
      setPermission(Notification.permission);
      navigator.serviceWorker.ready.then((reg) => {
        reg.pushManager.getSubscription().then((sub) => {
          setSubscription(sub);
        });
      });
    }
  }, []);

  const subscribe = useCallback(async () => {
    if (!supported || !VAPID_KEY) return null;
    setLoading(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') {
        setLoading(false);
        return null;
      }

      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_KEY),
      });
      setSubscription(sub);

      // Register with backend
      await axios.post(`${API}/api/push/devices/register`, {
        push_subscription: sub.toJSON(),
        device_type: 'web',
        platform: navigator.userAgent.includes('Mobile') ? 'mobile_web' : 'desktop_web',
        app_version: '1.0.0',
      }, { withCredentials: true });

      setLoading(false);
      return sub;
    } catch (e) {
      console.error('Push subscription failed:', e);
      setLoading(false);
      return null;
    }
  }, [supported]);

  const unsubscribe = useCallback(async () => {
    if (!subscription) return;
    setLoading(true);
    try {
      const endpoint = subscription.endpoint;
      await subscription.unsubscribe();
      setSubscription(null);
      await axios.post(`${API}/api/push/devices/unregister`, { endpoint }, { withCredentials: true });
    } catch (e) {
      console.error('Unsubscribe failed:', e);
    }
    setLoading(false);
  }, [subscription]);

  const testPush = useCallback(async () => {
    try {
      const r = await axios.post(`${API}/api/push/test`, {}, { withCredentials: true });
      return r.data;
    } catch (e) {
      console.error('Test push failed:', e);
      return null;
    }
  }, []);

  return {
    supported,
    permission,
    subscription,
    isSubscribed: !!subscription,
    loading,
    subscribe,
    unsubscribe,
    testPush,
  };
}
